import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, TextField, IconButton, Chip, Tooltip, Popover, Slider, Typography, useTheme, keyframes } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Memory } from '@mui/icons-material';
import { trackEvent, AnalyticsEvents } from '../../utils/analytics';

// --- New, Self-Contained ThoughtLimitControl Component ---

const thoughtSteps = [2, 4, 8, 16, 32]; // Exponential steps for the slider

// Converts a step index (0, 1, 2...) to the actual value (4, 8, 16...)
const stepIndexToValue = (index: number) => thoughtSteps[index] || thoughtSteps[0];

// Converts an actual value (4, 8, 16...) back to its index for the slider
const valueToStepIndex = (value: number) => {
  const index = thoughtSteps.indexOf(value);
  return index === -1 ? 0 : index;
};

interface ThoughtLimitControlProps {
  value: number;
  onChange: (newValue: number) => void;
  disabled?: boolean;
}

const breatheAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 4px rgba(0, 123, 255, 0.2);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 10px rgba(0, 123, 255, 0.5);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 4px rgba(0, 123, 255, 0.2);
  }
`;

// --- New, Self-Contained Animated ThoughtLimitControl Component ---

const ThoughtLimitControl = ({ value, onChange, disabled }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<any>(null);
  const theme = useTheme();

  // Define dimensions for easy access
  const controlSize = 40;
  const expandedHeight = 180;
  const trackHeight = expandedHeight - controlSize;

  // Calculates and sets the new value based on a mouse/touch event position
  const updateValueFromEvent = useCallback((event: any) => {
    if (!containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const draggableTop = containerRect.top - (expandedHeight - controlSize);
    const draggableBottom = containerRect.top;
    const draggableAreaHeight = draggableBottom - draggableTop;

    const clientY = event.touches ? event.touches[0].clientY : event.clientY;
    let y = clientY - draggableTop;
    y = Math.max(0, Math.min(draggableAreaHeight, y));
    const percentage = 1 - (y / draggableAreaHeight);

    const stepIndex = Math.round(percentage * (thoughtSteps.length - 1));
    const newValue = stepIndexToValue(stepIndex);
    onChange(newValue);
  }, [onChange, controlSize, expandedHeight]);

  // This handler now only sets the initial state for dragging
  const handleDragStart = useCallback((event: any) => {
    if (disabled) return;
    setIsOpen(true);
    setIsDragging(true);
    event.preventDefault();
    updateValueFromEvent(event.nativeEvent);
  }, [disabled, updateValueFromEvent]);

  // This effect now manages the event listeners, preventing stale state issues
  useEffect(() => {
    const handleDragMove = (event: any) => {
      // preventDefault is needed for touch events to avoid scrolling the page
      event.preventDefault();
      updateValueFromEvent(event);
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      setTimeout(() => {
        if (containerRef.current && !containerRef.current.matches(':hover')) {
          setIsOpen(false);
        }
      }, 500);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchend', handleDragEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, updateValueFromEvent]);
  
  // Effect to handle closing the control when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (isDragging) return;
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDragging]);

  const springyEasing = 'cubic-bezier(.5, 1.5, .5, 1)';
  const transitionDuration = '350ms';

  const currentStepIndex = valueToStepIndex(value);
  const fillPercentage = currentStepIndex / (thoughtSteps.length - 1);
  const fillHeight = controlSize + (trackHeight * fillPercentage);

  return (
    <Box
      ref={containerRef}
      onMouseEnter={() => !disabled && setIsOpen(true)}
      onMouseLeave={() => !disabled && !isDragging && setIsOpen(false)}
      sx={{
        position: 'relative',
        width: controlSize,
        height: controlSize,
        flexShrink: 0,
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none',
        opacity: disabled ? 0.5 : 1,
        zIndex: isOpen ? 10 : 1,
      }}
    >
      <Box
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        sx={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          height: isOpen ? fillHeight : controlSize,
          backgroundColor: isOpen ? 'primary.main' : (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200'),
          borderRadius: isOpen ? `${controlSize / 2}px` : '50%',
          transition: `height ${transitionDuration} ${springyEasing}, border-radius ${transitionDuration} ${springyEasing}, background-color 200ms ease`,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          cursor: isOpen ? (disabled ? 'default' : 'grab') : 'pointer',
           '&:active': {
            cursor: isOpen ? (disabled ? 'default' : 'grabbing') : 'pointer',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: controlSize,
            borderRadius: '50%',
            // boxShadow: 'inset 0px 8px 8px -4px rgba(0, 0, 0, 0.25)',
            opacity: isOpen ? 1 : 0,
            transition: 'opacity 200ms ease 100ms',
          }
        }}
      >
        <Box
          sx={{
            width: controlSize,
            height: controlSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            animation: isOpen && !isDragging ? `${breatheAnimation} 2s infinite ease-in-out` : 'none',
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: '0.9rem',
              color: isOpen ? 'primary.contrastText' : 'text.primary',
              transition: 'color 200ms ease',
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// --- Main ChatInput Component ---

interface ChatInputProps {
  onSendMessage: (message: string, thoughtLimit: number, files: File[]) => void;
  isLoading: boolean;
  deepLearning?: boolean;
  onToggleDeepLearning?: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({ 
  onSendMessage, 
  isLoading,
  deepLearning,
  onToggleDeepLearning 
}) => {
  const [input, setInput] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [thoughtLimit, setThoughtLimit] = useState<number>(4); // Default value

  const handleSend = () => {
    if ((input.trim() || files.length > 0) && !isLoading) {
      onSendMessage(input, thoughtLimit, files);
      setInput('');
      setFiles([]);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFiles(prev => [...prev, ...Array.from(event.target.files ?? [])]);
      event.target.value = '';
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeepLearningToggle = () => {
    if (onToggleDeepLearning) {
      onToggleDeepLearning();
      trackEvent(AnalyticsEvents.DeepLearningToggled, { enabled: !deepLearning });
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 1, sm: 2 },
        py: { xs: 1, sm: 2 },
        borderTop: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        bottom: 0,
        pb: 'max(env(safe-area-inset-bottom), 8px)',
        zIndex: 2,
      }}
    >
      {files.length > 0 && (
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {files.map((f, idx) => (
            <Chip key={idx} label={f.name} onDelete={() => handleRemoveFile(idx)} color="primary" />
          ))}
        </Box>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {onToggleDeepLearning && (
          <Tooltip title={deepLearning ? "Deep Learning On" : "Deep Learning Off"}>
            <IconButton
              color={deepLearning ? 'primary' : 'default'}
              onClick={handleDeepLearningToggle}
              disabled={isLoading}
            >
              <Memory />
            </IconButton>
          </Tooltip>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          multiple
        />
        <IconButton color="default" onClick={handleAttachClick} disabled={isLoading}>
          <AttachFileIcon />
        </IconButton>
        

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          multiline
          maxRows={5}
        />
        
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={isLoading || (!input.trim() && files.length === 0)}
        >
          <SendIcon />
        </IconButton>
        <ThoughtLimitControl value={thoughtLimit} onChange={setThoughtLimit} disabled={isLoading} />

      </Box>
    </Box>
  );
};

export default ChatInput;